import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Bell, Check, Briefcase, MessageSquare, DollarSign, CheckCircle, XCircle, Rocket, PartyPopper, Unlock, Trash2, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const notificationIcons = {
  new_job: Briefcase,
  job_application: Briefcase,
  new_message: MessageSquare,
  quote_sent: DollarSign,
  quote_accepted: CheckCircle,
  quote_rejected: XCircle,
  job_started: Rocket,
  job_completed: PartyPopper,
  lead_purchased: Unlock,
};

const notificationColors = {
  new_job: "bg-blue-100 text-blue-600",
  job_application: "bg-purple-100 text-purple-600",
  new_message: "bg-green-100 text-green-600",
  quote_sent: "bg-yellow-100 text-yellow-600",
  quote_accepted: "bg-green-100 text-green-600",
  quote_rejected: "bg-red-100 text-red-600",
  job_started: "bg-blue-100 text-blue-600",
  job_completed: "bg-green-100 text-green-600",
  lead_purchased: "bg-purple-100 text-purple-600",
};

const NotificationsPage = () => {
  const [searchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [lookupEmail, setLookupEmail] = useState(searchParams.get("email") || "");
  const [filter, setFilter] = useState("all"); // all, unread

  useEffect(() => {
    // Check for saved contractor info
    const savedEmail = localStorage.getItem("workerEmail");
    const savedWorkerId = localStorage.getItem("workerId");
    
    if (savedWorkerId && savedEmail) {
      setEmail(savedEmail);
      fetchNotifications("contractor", savedWorkerId);
    } else if (lookupEmail) {
      setEmail(lookupEmail);
      fetchNotificationsByEmail(lookupEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupEmail]);

  const fetchNotifications = async (userType, userId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications/${userType}/${userId}`);
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationsByEmail = async (emailToLookup) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications/email/${encodeURIComponent(emailToLookup)}`);
      setNotifications(response.data.notifications || []);
      setEmail(emailToLookup);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = (e) => {
    e.preventDefault();
    if (lookupEmail) {
      fetchNotificationsByEmail(lookupEmail);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    const savedWorkerId = localStorage.getItem("workerId");
    if (!savedWorkerId) return;
    
    try {
      await axios.patch(`${API}/notifications/mark-all-read?user_type=contractor&user_id=${savedWorkerId}`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success("Notification deleted");
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error("Failed to delete notification");
    }
  };

  const filteredNotifications = filter === "unread" 
    ? notifications.filter(n => !n.read) 
    : notifications;

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Navbar />

      <div className="container-custom py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-[#202124]">Notifications</h1>
              {email && (
                <p className="text-sm text-[#5f6368] mt-1">
                  Showing notifications for {email}
                </p>
              )}
            </div>
            {unreadCount > 0 && (
              <Button
                onClick={markAllAsRead}
                variant="outline"
                className="rounded-full"
              >
                <Check className="w-4 h-4 mr-2" />
                Mark all as read
              </Button>
            )}
          </div>

          {/* Email Lookup (for customers) */}
          {!localStorage.getItem("workerId") && (
            <div className="bg-white rounded-xl border border-[#dadce0] p-4 mb-6">
              <form onSubmit={handleLookup} className="flex gap-3">
                <Input
                  type="email"
                  placeholder="Enter your email to view notifications"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit" className="bg-[#1a73e8] hover:bg-[#1557b0]">
                  Look Up
                </Button>
              </form>
            </div>
          )}

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === "all"
                  ? "bg-[#1a73e8] text-white"
                  : "bg-white text-[#5f6368] hover:bg-[#f1f3f4]"
              }`}
            >
              All ({notifications.length})
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === "unread"
                  ? "bg-[#1a73e8] text-white"
                  : "bg-white text-[#5f6368] hover:bg-[#f1f3f4]"
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>

          {/* Notifications List */}
          <div className="bg-white rounded-xl border border-[#dadce0] overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#1a73e8] mx-auto" />
                <p className="text-[#5f6368] mt-2">Loading notifications...</p>
              </div>
            ) : !email ? (
              <div className="p-12 text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#5f6368]">Enter your email to view notifications</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="p-12 text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#5f6368]">
                  {filter === "unread" ? "No unread notifications" : "No notifications yet"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[#f1f3f4]">
                {filteredNotifications.map((notification) => {
                  const Icon = notificationIcons[notification.type] || Bell;
                  const colorClass = notificationColors[notification.type] || "bg-gray-100 text-gray-600";
                  
                  return (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-[#f8f9fa] transition-colors ${
                        !notification.read ? "bg-[#e8f0fe]" : ""
                      }`}
                    >
                      <div className="flex gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className={`${!notification.read ? "font-semibold" : ""} text-[#202124]`}>
                                {notification.title}
                              </p>
                              <p className="text-sm text-[#5f6368] mt-1">
                                {notification.message}
                              </p>
                              <p className="text-xs text-[#80868b] mt-2">
                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {!notification.read && (
                                <button
                                  onClick={() => markAsRead(notification.id)}
                                  className="p-1.5 rounded-full hover:bg-[#e8f0fe] text-[#1a73e8]"
                                  title="Mark as read"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => deleteNotification(notification.id)}
                                className="p-1.5 rounded-full hover:bg-red-50 text-[#5f6368] hover:text-red-500"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {notification.link && (
                            <Link
                              to={notification.link}
                              className="inline-block mt-3 text-sm text-[#1a73e8] hover:underline"
                            >
                              View details →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default NotificationsPage;
